import { memo, useState } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import dayjs from 'dayjs';
import { ICluster, IContent } from 'src/interface';
import { formatTime } from 'src/utils/date';
import { isOpenSelector } from 'src/selectors/content';
import { Element, RouterLink } from 'src/components';
import { ContentPublisher } from 'src/modules/content/components/shared/ContentPublisher';
import { ContentMoreAction } from '../ContentAction/more';
import styles from './style.module.scss';
import { ContentCluster } from 'src/modules/content/components/shared/ContentCluster';

const AudioSpeech = dynamic(() => import('../AudioSpeech'), {
    ssr: false,
});

interface IProps {
    content: IContent;
}

const ContentMeta = ({ content = {} as IContent }: IProps) => {
    const { publishedDate, publisher, cluster, speechs } = content;
    const isOpen = useSelector(isOpenSelector);
    const [isPlaying, setPlaying] = useState<boolean>(false);

    if (isOpen) {
        return (
            <Element
                className={cn(
                    styles['content-meta'],
                    'flex items-center',
                    'mb-[12px]',
                    'text-[13px] leading-[1] text-[#666666]',
                    'font-semibold',
                    'whitespace-nowrap',
                    {
                        '[&>*:not(:last-child)]:!mr-[12px]': !isOpen,
                    },
                )}
            >
                {publishedDate && (
                    <Element
                        renderAs="time"
                        dateTime={dayjs(publishedDate * 1000).toISOString()}
                    >
                        {formatTime(publishedDate)}
                    </Element>
                )}
                <ContentCluster cluster={cluster as ICluster} />
                <RouterLink to={content.redirectUrl} newTab={true}>
                    <Element renderAs="span">Bài gốc</Element>
                </RouterLink>
                <Element>
                    {speechs?.length && (
                        <AudioSpeech
                            speechs={speechs}
                            setPlaying={setPlaying}
                            isPlaying={isPlaying}
                        />
                    )}
                </Element>
            </Element>
        );
    }
    return (
        <Element
            className={cn(
                styles['content-meta'],
                'flex',
                'flex-col',
                'mb-[12px]',
                'text-[13px] leading-[1] text-[#666666]',
                'font-semibold',
                'whitespace-nowrap',
            )}
        >
            <Element
                className={cn(
                    'flex items-center',
                    'mb-[12px] pb-[8px]',
                    'border-b border-solid border-[rgba(0_0_0_0.05)]',
                    '!mr-0',
                )}
            >
                {publisher && (
                    <RouterLink to={publisher.url}>
                        <ContentPublisher
                            publisher={publisher}
                            hStyle={24}
                            mhStyle={16}
                            lazyloadImage={false}
                        />
                    </RouterLink>
                )}
                <Element className={'ml-auto'}>
                    {speechs?.length && (
                        <AudioSpeech
                            speechs={speechs}
                            setPlaying={setPlaying}
                            isPlaying={isPlaying}
                        />
                    )}

                    <ContentMoreAction {...content} />
                </Element>
            </Element>
            <Element
                className={cn(
                    'flex items-center',
                    '[&>*:not(:last-child)]:mr-[16px]',
                )}
            >
                {publishedDate && (
                    <Element
                        renderAs="time"
                        dateTime={dayjs(publishedDate * 1000).toISOString()}
                    >
                        {formatTime(publishedDate)}
                    </Element>
                )}
                <ContentCluster cluster={cluster as ICluster} />
                <RouterLink to={content.redirectUrl} newTab={true}>
                    <Element renderAs="span">Bài gốc</Element>
                </RouterLink>
            </Element>
        </Element>
    );
};

export default memo(ContentMeta);
